import {
  Column,
  GridApi,
  IRowNode,
  SuppressKeyboardEventParams,
} from 'ag-grid-community';

import { GridContext } from '../interfaces/grid-context';

export const SupressKeyboard = {
  delete: (params: SuppressKeyboardEventParams): boolean => {
    const context = params.context as GridContext;
    if (context.rowDeleteFn) {
      context.rowDeleteFn(params);
    }
    return true;
  },
  enter: (params: SuppressKeyboardEventParams): boolean => {
    const { api, column, event, node } = params;
    let suppress = false;

    if (isEditing(params)) {
      const columns = api.getAllGridColumns();
      const editableColumns = columns.filter(
        col => col.isCellEditable(node) && !!col.isVisible()
      );
      const lastVisibleEditableColumn =
        editableColumns[editableColumns.length - 1];
      const isLastEditableColumn = lastVisibleEditableColumn === column;
      const isLastRow = node.rowIndex === api.getLastDisplayedRowIndex();

      if (!isLastEditableColumn) {
        event.preventDefault();
        event.shiftKey ? api.tabToPreviousCell() : api.tabToNextCell();
        suppress = true;
      } else if (!isLastRow) {
        if (node.isRowPinned()) return true;
        suppress = true;
        // select one row below if enter is pressed on last editable column while inline edit
        if (params.node.rowIndex || params.node.rowIndex === 0) {
          api.stopEditing();
          // wait for editingStopped is executed for the prev cell (grid-row-edit directive)
          setTimeout(() => {
            api
              .getDisplayedRowAtIndex(params.node.rowIndex! + 1)
              ?.setSelected(true, true);
            const selectedNode = params.api.getSelectedNodes()[0];
            startEditOnFirstEditableCell(params, selectedNode);
          });

          return suppress;
        }
      }
    } else {
      if (node.isRowPinned()) return true;
      api.stopEditing();
      suppress = startEditOnFirstEditableCell(params);
    }
    return suppress;
  },
};

const startEdit = (api: GridApi, column: Column, node: IRowNode): void => {
  const { rowIndex, rowPinned } = node;
  api.clearCellSelection();
  api.ensureColumnVisible(column);
  api.setFocusedCell(rowIndex!, column);
  api.startEditingCell({ colKey: column, rowIndex: rowIndex!, rowPinned });
};

const startEditOnFirstEditableCell = (
  params: SuppressKeyboardEventParams,
  selectedNode?: IRowNode
): boolean => {
  let suppress = false;
  const firstEditableCell = params.api
    .getAllDisplayedColumns()
    .find(c => c.isCellEditable(params.node));
  const firstEditableColumn = getFirstEditableColumn(params);

  if (firstEditableCell && firstEditableColumn) {
    params.api.clearCellSelection();
    params.api.setFocusedCell(
      params.node.rowIndex!,
      firstEditableCell.getColId()
    );
    startEdit(params.api, firstEditableColumn, selectedNode || params.node);
    suppress = true;
  }
  return suppress;
};

const getFirstEditableColumn = (
  params: SuppressKeyboardEventParams
): Column | undefined => {
  const { event, node, api } = params;
  const columns = api.getAllDisplayedColumns();
  return event.shiftKey
    ? [...columns].reverse().find(c => c.isCellEditable(node))
    : columns.find(c => c.isCellEditable(node));
};

const isEditing = ({ api, editing }: SuppressKeyboardEventParams): boolean =>
  editing || api.getEditingCells().length > 0;
